import {
  Box,
  Typography,
  Card,
  Grid,
  Chip,
  IconButton,
  TextField,
  Button,
  InputAdornment,
  Paper,
  useMediaQuery,
  Pagination,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SpaIcon from "@mui/icons-material/Spa";
import { useNavigate } from "react-router-dom";
import { getAllFarmersAction } from "../../../store/actions";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

export default function AMRMFarmerManagement() {
  const navigate = useNavigate();
  const dispatch = useDispatch()

  const isMobile = useMediaQuery('(max-width:600px)');

  const { successData } = useSelector((state) => state.getAllFarmersReducer);

  useEffect(() => {
    if (getAllFarmersAction) {
      dispatch(getAllFarmersAction());
    }
  }, [dispatch]);

  // Map API response to UI-friendly structures
  const farmers = successData?.data?.farmers || [];
  const totalCount = successData?.data?.totalFarmerCount ?? successData?.data?.count ?? farmers.length;
  const totalFarmerAcres = Number(successData?.data?.totalFarmerAcres ?? 0);
  const expectedAcresCount = Number(successData?.data?.expectedAcresCount ?? 0);

  const stats = [
    { label: 'TOTAL FARMERS', value: totalCount.toLocaleString('en-IN'), icon: <PeopleIcon />, bg: '#EEF4FF' },
    { label: 'TOTAL ACRES', value: totalFarmerAcres.toLocaleString('en-IN'), icon: <SpaIcon />, bg: '#FFF1F1' },
    { label: 'TOTAL COMMITTED ACRES', value: expectedAcresCount.toLocaleString('en-IN'), icon: <AccessTimeIcon />, bg: '#FFF8E6' },
      { label: 'TOTAL ACRES SOWN', value:  (successData?.data?.totalPurchaseQty ?? 0).toLocaleString('en-IN'), icon: <CheckCircleIcon />, bg: '#E9FBF0' },
  ];

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;

  const rows = farmers.map((f) => ({
    name: f?.name || '-',
    code: f?.geoCode || f?.farmerId || '-',
    farmerId: f?.farmerId,
    phone: f?.mobile || '-',
    acres: (() => {
      const val = Number.parseFloat(f?.totalAcres);
      if (isNaN(val)) return '0';
      return val % 1 === 0 ? val.toFixed(0) : val.toString();
    })(),
    irrigation: f?.irrigationType
      ? `${f.irrigationType}${f?.acresUnderIrrigation ? ` (${f.acresUnderIrrigation} Ac)` : ''}`
      : '-',
    focus: f?.focusCrop || '-',
    crops: f?.majorCrops || [],
    status: ((f?.status || '').toUpperCase() === 'ACTIVE') ? 'Active'
      : ((f?.status || '').toUpperCase().startsWith('PENDING')) ? 'Pending'
        : (f?.status || 'Unknown'),
  }));

  const filteredRows = rows.filter((row) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      String(row.name).toLowerCase().includes(q) ||
      String(row.code).toLowerCase().includes(q) ||
      String(row.phone).toLowerCase().includes(q) ||
      String(row.focus).toLowerCase().includes(q) ||
      String(row.status).toLowerCase().includes(q)
    );
  });

  // Reset page on search change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Box sx={{ minHeight: "100vh", p:2}}>
      {/* Header */}
      <Box mb={1.5} mt={{ xs: 7, sm: 10, md: 0 }}>
        <Typography variant="h6" fontWeight={600}  >
          Farmer Management
        </Typography>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} mb={3}>
        {stats.map((s) => (
          <Grid item size={{ xs: 6, sm: 3, md: 3 }} key={s.label}>
            <Card
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid #E5E7EB',
                height: { xs: 75, sm: 75,md: 82,lg:60 },
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {s.label}
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {s.value}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: s.bg,
                    p: 1.2,
                    borderRadius: 2,
                  }}
                >
                  {s.icon}
                </Box>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Farmer Search & Add Button Only */}
      <Card
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          bgcolor: "#fff",
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          boxShadow: 'none',

        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent='space-between'
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={2}
        >
          <TextField
            placeholder="Search ..."
            size="small"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e)}
            sx={{ width: { xs: "100%", sm: 380 }, bgcolor: '#fff' }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: 3 }
              }
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              bgcolor: "#5B4CF5",
              px: 3,
              py: 1,
              borderRadius: 3,
              textTransform: "none",
              mt: { xs: 1, sm: 0 },
              width: { xs: "100%", sm: "auto" },
              fontWeight: 600,
              boxShadow: 'none',
            }}
            onClick={() => navigate('/amrm/farmerManagementcreate')}
          >
            Add New Farmer
          </Button>
        </Box>
      </Card>

      {/* Responsive List/Table */}
      {isMobile ? (
        <Box>
          {pagedRows.map((row) => (
            <Card key={row.code} sx={{ mb: 2, borderRadius: 3, p: 2 }}>
              {/* Farmer and Status */}
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                <Box>
                  <Typography>
                    <span style={{ fontWeight: 600 }}>Farmer:</span> {row.name}
                  </Typography>
                </Box>
                <Chip
                  label={row.status}
                  sx={{
                    width: 80,
                    backgroundColor:
                      row.status === "Active"
                        ? "#D1FAE5"
                        : row.status === "Pending"
                          ? "#FFEFD6"
                          : "default",
                    color:
                      row.status === "Active"
                        ? "#047857"
                        : row.status === "Pending"
                          ? "#B45309"
                          : undefined,
                    fontWeight: 600,
                  }}
                  variant="filled"
                  size="small"
                />
              </Box>

              {/* Phone */}
              <Box mb={0.5}>
                <Typography variant="body2"><strong>Phone:</strong> {row.phone}</Typography>
              </Box>
              {/* Agriculture */}
              <Box mb={0.5}>
                <Typography variant="body2"><strong>Agriculture:</strong> {row.acres} Acres</Typography>
              </Box>
              {/* Crop */}
              <Box mb={0.5}>
                <Typography variant="body2"><strong>Crop:</strong> {row.focus}</Typography>
              </Box>

              {/* Actions */}
              <Box display="flex" justifyContent="flex-end" mt={1} gap={0.5}>
                
                <IconButton
                  size="small"
                  aria-label="edit"
                  onClick={() => navigate('/amrm/farmerManagementcreate', { state: { farmerId: row.farmerId } })}
                >
                  <EditOutlinedIcon fontSize="small" sx={{ color: "#2563EB", filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                </IconButton>
              </Box>
            </Card>
          ))}
          {filteredRows.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>No farmers found.</Typography>
          )}
          {totalPages > 1 && (
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={1}>
              <Typography variant="body2" >
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredRows.length)} to {Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length} entries
              </Typography>
              <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" size="small" />
            </Box>
          )}
        </Box>
      ) : (
        <Box>
          {/* Table Header: visible only on sm and up */}
          <Box
            display={{ xs: "none", sm: "grid" }}
            gridTemplateColumns={{ sm: "2fr 2fr 2fr 1fr 1fr 1fr" }}
            px={{ xs: 1, sm: 2 }}
            mb={{ xs: 1, sm: 1 }}
          >
            {[
              "Farmer Identity",
              "Contact & Location",
              "Agricultural Info",
              "Crop",
              "Status",
              "Actions",
            ].map((h) => (
              <Typography key={h} fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>
                {h.toUpperCase()}
              </Typography>
            ))}
          </Box>

          {/* Rows */}
          {pagedRows.map((row) => (
            <Paper
              key={row.code}
              elevation={0}
              sx={{
                mb: { xs: 1, sm: 2 },
                p: { xs: 1, sm: 1.5 },
                borderRadius: 3,
                display: { xs: "block", sm: "grid" },
                gridTemplateColumns: { sm: "2fr 2fr 2fr 1fr 1fr 1fr" },
                alignItems: "center",
                border: "1px solid #E5E7EB",
                gap: { xs: 1, sm: 0 },
                '&:hover': {
                  border: '1.5px solid #2563EB',
                  boxShadow: '0 4px 16px 0 rgba(37,99,235,0.15)',
                },
              }}
            >
              {/* Farmer Identity */}
              <Box display={{ xs: "none", sm: "flex" }} gap={2} alignItems="center">
                <Box>
                  <Typography fontWeight={500} fontSize={{ sm: 16 }}>{row.name}</Typography>
                </Box>
              </Box>

              {/* Contact & Location */}
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Typography variant="body2">{row.phone}</Typography>
              </Box>

              {/* Agricultural Info */}
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Typography fontWeight={600} fontSize={{ sm: 14 }}>
                  {row.acres} <span style={{ fontWeight: 400 }}>Acres</span>
                </Typography>
              </Box>

              {/* Crop */}
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Typography variant="body2">{row.focus}</Typography>
              </Box>

              {/* Status */}
              <Chip
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  width: { sm: 80 },
                  backgroundColor:
                    row.status === "Active"
                      ? "#D1FAE5" // light green
                      : row.status === "Pending"
                        ? "#FFEFD6" // light orange
                        : "default",
                  color:
                    row.status === "Active"
                      ? "#047857"
                      : row.status === "Pending"
                        ? "#B45309"
                        : undefined,
                  fontWeight: 600,
                }}
                label={row.status}
                size="small"
              />

              {/* Actions */}
              <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 1, justifyContent: "flex-start" }}>
                <IconButton
                  size="small"
                  onClick={() => navigate('/amrm/farmerManagementcreate', { state: { farmerId: row.farmerId } })}
                >
                  <EditOutlinedIcon fontSize="small" sx={{ color: "#2563EB", filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                </IconButton>
              </Box>
            </Paper>
          ))}
          {filteredRows.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>No farmers found.</Typography>
          )}
          {totalPages > 1 && (
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={1}>
              <Typography variant="body2" >
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredRows.length)} to {Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length} entries
              </Typography>
              <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
